import * as React from 'react';
import {
    ActivityHistoryCard,
    useL2DTheme,
    useModal,
    type ActivityProps,
} from '@europrocurement/flexy-components';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ConfirmationModal } from '@europrocurement/flexy-components/components/organisms/ConfirmationModal';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { useSnackbar } from 'notistack';
import {
    type Prescripteur,
    type PrescripteurNote,
    useSociete,
    useTiersService,
} from '@europrocurement/l2d-domain';
import ActivitiesCardContent from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/ActivitiesCardContent/ActivitiesCardContent';
import {
    addNoteIcon,
    addTaskIcon,
    forbiddenIcon,
    NamedIconsType,
    preview,
} from '@europrocurement/l2d-icons';
import TasksCardContent from '@europrocurement/l2d-modules/modules/Tasks/components/TasksCardContent/TasksCardContent';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { useActionLogger, useResolveUserByKeycloakId } from '@europrocurement/l2d-modules';
import { LogActionTiersLogActionDtoJsonldLogRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

export type ActivityHistorySectionProps = {
    prescriber: Prescripteur;
    requestLogActionLimited?: boolean;
};

/**
 * Formate le nom et prénom d'un utilisateur selon les conventions d'affichage
 */
const formatUserDisplayName = (
    firstName?: string,
    lastName?: string,
): { firstName: string; lastName: string } => {
    if (!firstName || !lastName) {
        return { firstName: '', lastName: '' };
    }

    return {
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase(),
        lastName: lastName.toUpperCase(),
    };
};

export const ActivityHistorySection: React.FunctionComponent<ActivityHistorySectionProps> =
    function ({ prescriber, requestLogActionLimited = true }) {
        const { prescribersApi } = useTiersService();
        const xIdSociete = useSociete();
        const keycloakService = UseKeycloakService();
        const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;

        const [activities, setActivities] = React.useState<ActivityProps[]>([]);
        const [isLoading, setIsLoading] = React.useState(true);
        const [isNotesLoading, setIsNotesLoading] = React.useState(false);
        const [notesError, setNotesError] = React.useState<string | null>(null);
        const [isEditingNote, setIsEditingNote] = React.useState(false);
        const [isDeletingNote, setIsDeletingNote] = React.useState(false);
        const [showLogsTechniques, setShowLogsTechniques] = React.useState<boolean>(false);
        const [showIconTechniques, setShowIconTechniques] = React.useState<NamedIconsType>(preview);
        const [textLogsTechnique, setTextLogsTechnique] = React.useState<string>(
            'Afficher les actions techniques',
        );
        const { modalActions } = useModal();
        const { palette } = useL2DTheme();
        const { enqueueSnackbar } = useSnackbar();
        const { getLogActionTiers } = useActionLogger();
        const { resolveUserByKeycloakId } = useResolveUserByKeycloakId();

        const convertNoteToActivity = React.useCallback(
            async (note: PrescripteurNote): Promise<ActivityProps> => {
                const createdByUser = note.createdBy
                    ? await resolveUserByKeycloakId(note.createdBy)
                    : null;
                const updatedByUser = note.updatedBy
                    ? await resolveUserByKeycloakId(note.updatedBy)
                    : null;

                const createdByFormatted = createdByUser
                    ? formatUserDisplayName(createdByUser.firstName, createdByUser.lastName)
                    : { firstName: note.createdBy || 'Anonyme', lastName: '' };

                const updatedByFormatted = updatedByUser
                    ? formatUserDisplayName(updatedByUser.firstName, updatedByUser.lastName)
                    : { firstName: note.updatedBy || '', lastName: '' };

                const userId = createdByUser?.keycloakUserIdentifier || note.createdBy || 'unknown';

                return {
                    id: `note-${note.id}`,
                    type: 'note',
                    noteType: note.highlighted ? 'important' : 'standard',
                    content: note.content || '',
                    createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                    userId,
                    userFirstName: createdByFormatted.firstName,
                    userLastName: createdByFormatted.lastName,
                    updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                    updatedBy: note.updatedBy || undefined,
                    updatedByFirstName: updatedByFormatted.firstName,
                    updatedByLastName: updatedByFormatted.lastName,
                    memoClient: false,
                };
            },
            [resolveUserByKeycloakId],
        );

        /**
         * Charge les notes depuis l'API Tiers
         */
        const loadNotesFromApi = React.useCallback(async (): Promise<PrescripteurNote[]> => {
            try {
                setIsNotesLoading(true);
                setNotesError(null);

                if (!prescriber?.id) {
                    console.warn('Aucun ID de prescripteur fourni');
                    return [];
                }

                const response = await prescribersApi.getNotesPrescripteurTiersItem({
                    id: prescriber.id,
                    xIdSociete,
                });

                if (!response?.data?.notes) {
                    console.warn('Structure de réponse inattendue pour les notes:', response);
                    return [];
                }

                return response.data.notes;
            } catch (error: unknown) {
                console.error("Erreur lors du chargement des notes depuis l'API Tiers:", error);
                const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
                setNotesError(`Erreur lors du chargement des notes: ${errorMessage}`);
                return [];
            } finally {
                setIsNotesLoading(false);
            }
        }, [prescriber?.id, prescribersApi, xIdSociete]);

        /**
         * Convertit un log en activité
         */
        const createLogActivity = React.useCallback(
            async (item: LogActionTiersLogActionDtoJsonldLogRead): Promise<ActivityProps> => {
                const uid = item.idUserKeycloak ?? '';
                const user = uid ? await resolveUserByKeycloakId(uid) : null;

                return {
                    type: 'action',
                    userFirstName: uid ? (user?.firstName ?? uid) : '',
                    userLastName: uid ? (user?.lastName ?? '') : '',
                    actionType: item.actionType?.logCategories?.[0]?.category ?? '',
                    createdAt: item.date ? new Date(item.date as string) : new Date(),
                    id: String(item['@id'] ?? 0),
                    objectType: item.actionType?.actionTypeLibelle ?? '',
                    description: item.logMessage ?? '',
                    memoClient: false,
                } as ActivityProps;
            },
            [resolveUserByKeycloakId],
        );

        /**
         * Charge toutes les activités (notes converties)
         */
        const loadActivities = React.useCallback(
            async () => {
                if (!prescriber?.id) {
                    setIsLoading(false);
                    return;
                }

                setIsLoading(true);

                try {
                    const notes = await loadNotesFromApi();
                    const logsData = await getLogActionTiers(
                        prescriber.id,
                        requestLogActionLimited,
                        showLogsTechniques,
                    );
                    const noteActivities: ActivityProps[] = await Promise.all(
                        notes.map(convertNoteToActivity),
                    );
                    const logActivities = await Promise.all(logsData.map(createLogActivity));

                    const sortedActivities = [...noteActivities, ...logActivities].sort(
                        (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
                    );

                    setActivities(sortedActivities);
                } catch (error: unknown) {
                    console.error('Erreur lors du chargement des activités:', error);
                    setNotesError('Erreur lors du chargement des activités');
                } finally {
                    setIsLoading(false);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                prescriber?.id,
                loadNotesFromApi,
                convertNoteToActivity,
                createLogActivity,
                showLogsTechniques,
            ],
        );
        /**
         * Rafraîchit les activités après une modification
         */
        const refreshActivities = React.useCallback(async () => {
            try {
                const notes = await loadNotesFromApi();

                const noteActivities: ActivityProps[] = await Promise.all(
                    notes.map(convertNoteToActivity),
                );

                const sortedActivities = noteActivities.sort(
                    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
                );

                setActivities(sortedActivities);
            } catch (error: unknown) {
                console.error('Erreur lors du rafraîchissement des notes:', error);
            }
        }, [loadNotesFromApi, convertNoteToActivity]);

        React.useEffect(() => {
            loadActivities();
        }, [loadActivities]);

        /**
         * Gère l'ajout d'une nouvelle note
         */
        const handleAddNote = React.useCallback(() => {
            modalActions.call(
                <ModalCreateNote
                    onConfirm={async (content: string, highlighted: boolean) => {
                        if (!prescriber?.id) {
                            enqueueSnackbar(
                                'Impossible de créer la note : prescripteur non défini',
                                {
                                    variant: 'error',
                                },
                            );
                            return;
                        }

                        try {
                            await prescribersApi.addNotePrescripteurTiersItem({
                                id: prescriber.id.toString(),
                                addNoteContactContactsItemRequest: {
                                    content,
                                    highlighted,
                                },
                                xIdSociete,
                            });

                            await refreshActivities();
                            modalActions.close();
                            enqueueSnackbar('Note créée avec succès');
                        } catch (error: unknown) {
                            console.error('Erreur lors de la création de la note:', error);
                            const errorMessage =
                                error instanceof Error ? error.message : 'Erreur inconnue';
                            enqueueSnackbar(
                                `Erreur lors de la création de la note: ${errorMessage}`,
                                {
                                    variant: 'error',
                                },
                            );
                        }
                    }}
                />,
            );
        }, [
            prescriber?.id,
            prescribersApi,
            xIdSociete,
            refreshActivities,
            modalActions,
            enqueueSnackbar,
        ]);

        /**
         * Gère l'édition d'une note existante
         */
        const handleEditNote = React.useCallback(
            (noteId: string, content: string, highlighted: boolean) => {
                modalActions.call(
                    <ModalEditNote
                        initialContent={content}
                        initialHighlighted={highlighted}
                        isSaving={isEditingNote}
                        onConfirm={async (newContent: string, newHighlighted: boolean) => {
                            if (!prescriber?.id) {
                                enqueueSnackbar(
                                    'Impossible de modifier la note : prescripteur non défini',
                                    {
                                        variant: 'error',
                                    },
                                );
                                return;
                            }

                            try {
                                setIsEditingNote(true);

                                await prescribersApi.updateNotePrescripteurTiersItem({
                                    id: prescriber.id,
                                    noteId: +noteId.replace('note-', ''),
                                    addNoteContactContactsItemRequest: {
                                        content: newContent,
                                        highlighted: newHighlighted,
                                    },
                                    xIdSociete,
                                });

                                await refreshActivities();
                                modalActions.close();
                                enqueueSnackbar('Note mise à jour avec succès');
                            } catch (error: unknown) {
                                console.error('Erreur lors de la modification de la note:', error);

                                const isUnauthorized =
                                    (error &&
                                        typeof error === 'object' &&
                                        'status' in error &&
                                        error.status === 403) ||
                                    (error &&
                                        typeof error === 'object' &&
                                        'response' in error &&
                                        error.response &&
                                        typeof error.response === 'object' &&
                                        'status' in error.response &&
                                        error.response.status === 403);

                                if (isUnauthorized) {
                                    enqueueSnackbar(
                                        "Vous n'avez pas les droits pour modifier cette note",
                                        {
                                            variant: 'error',
                                        },
                                    );
                                } else {
                                    const errorMessage =
                                        error instanceof Error ? error.message : 'Erreur inconnue';
                                    enqueueSnackbar(
                                        `Erreur lors de la modification de la note: ${errorMessage}`,
                                        {
                                            variant: 'error',
                                        },
                                    );
                                }
                            } finally {
                                setIsEditingNote(false);
                            }
                        }}
                        onCancel={() => modalActions.close()}
                    />,
                );
            },
            [
                prescriber?.id,
                prescribersApi,
                xIdSociete,
                refreshActivities,
                modalActions,
                enqueueSnackbar,
                isEditingNote,
            ],
        );

        /**
         * Gère la suppression d'une note
         */
        const handleDeleteNote = React.useCallback(
            (noteId: string) => {
                modalActions.call(
                    <ConfirmationModal
                        messages={NOTE_DELETE_MESSAGES}
                        color={palette.error}
                        disabled={isDeletingNote}
                        actionOnValidation={async () => {
                            if (!prescriber?.id) {
                                enqueueSnackbar(
                                    'Impossible de supprimer la note : prescripteur non défini',
                                    {
                                        variant: 'error',
                                    },
                                );
                                return;
                            }

                            try {
                                setIsDeletingNote(true);

                                await prescribersApi.deleteNotePrescripteurTiersItem({
                                    id: prescriber.id,
                                    noteId: +noteId.replace('note-', ''),
                                    xIdSociete,
                                });

                                await refreshActivities();
                                modalActions.close();
                                enqueueSnackbar('Note supprimée avec succès');
                            } catch (error: unknown) {
                                console.error('Erreur lors de la suppression de la note:', error);

                                const isUnauthorized =
                                    (error &&
                                        typeof error === 'object' &&
                                        'status' in error &&
                                        error.status === 403) ||
                                    (error &&
                                        typeof error === 'object' &&
                                        'response' in error &&
                                        error.response &&
                                        typeof error.response === 'object' &&
                                        'status' in error.response &&
                                        error.response.status === 403);

                                if (isUnauthorized) {
                                    enqueueSnackbar(
                                        "Vous n'avez pas les droits pour supprimer cette note",
                                        {
                                            variant: 'error',
                                        },
                                    );
                                } else {
                                    const errorMessage =
                                        error instanceof Error ? error.message : 'Erreur inconnue';
                                    enqueueSnackbar(
                                        `Erreur lors de la suppression de la note: ${errorMessage}`,
                                        {
                                            variant: 'error',
                                        },
                                    );
                                }
                            } finally {
                                setIsDeletingNote(false);
                            }
                        }}
                        actionOnCancellation={() => modalActions.close()}
                    />,
                );
            },
            [
                prescriber?.id,
                prescribersApi,
                xIdSociete,
                refreshActivities,
                modalActions,
                enqueueSnackbar,
                isDeletingNote,
                palette.error,
            ],
        );

        const {
            orderedTasks,
            tasksLoading,
            handleAddTask,
            handleCheckTask,
            handleEditTask,
            handleDeleteTask,
        } = useTasksHook({
            apiTarget: 'tiers',
            linkedEntityId: prescriber['@id'] as string,
        });

        const handleLoadTechiques = () => {
            setShowLogsTechniques((prev) => !prev);
        };

        React.useEffect(() => {
            setTextLogsTechnique(
                showLogsTechniques
                    ? 'Masquer les actions techniques'
                    : 'Afficher les actions techniques',
            );
            setShowIconTechniques(showLogsTechniques ? forbiddenIcon : preview);

            // loadActivitiesTechniques(showLogsTechniques);
        }, [showLogsTechniques, showIconTechniques]);

        const actions = React.useMemo(
            () => [
                {
                    id: 'add-note',
                    label: 'Ajouter une note',
                    shortLabel: 'Ajouter',
                    icon: addNoteIcon,
                    onClick: handleAddNote,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                {
                    id: 'add-task',
                    label: 'Ajouter une tâche',
                    shortLabel: 'Ajouter',
                    icon: addTaskIcon,
                    onClick: handleAddTask,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                {
                    id: 'show-technique',
                    label: textLogsTechnique,
                    shortLabel: 'Afficher',
                    icon: showIconTechniques,
                    onClick: handleLoadTechiques,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
            ],
            [handleAddNote, handleAddTask, palette.grey, showIconTechniques, textLogsTechnique],
        );

        return (
            <ActivityHistoryCard actions={actions}>
                <TasksCardContent
                    tasks={orderedTasks}
                    tasksLoading={tasksLoading}
                    onCheckTask={handleCheckTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    userIdentifier={userIdentifier}
                />
                <ActivitiesCardContent
                    activities={activities}
                    onEditNote={handleEditNote}
                    onDeleteNote={handleDeleteNote}
                    activitiesLoading={isLoading}
                    notesLoading={isNotesLoading}
                    notesError={notesError}
                />
            </ActivityHistoryCard>
        );
    };
